import { useFormik } from 'formik';
import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseTextArea from 'components/atoms/base-textarea';
import BaseButton from 'components/atoms/baseButton';
import BaseSelectBox from 'components/atoms/base-select-box';
import { useTranslation } from 'react-i18next';
import { useEffect, type FC } from 'react';
import ConditionSearchSelect from 'components/molecules/conditions-search-select';
import MasterModal from 'components/organisms/masterModal';
import { TRACKING_PLATFORM_OPTIONS } from 'infrastructure/hooks/patient/use-tracking-platform';

import { patientConditionValidationSchema } from './validation-schema';
import s from './styles.module.scss';

import type { IConditionForUpdate } from 'infrastructure/interfaces';
import type { FormikHelpers } from 'formik';
import type { Values } from './validation-schema';

interface PatientConditionModalFormProps {
  open: boolean;
  onCancelForm: () => void;
  onSubmit: (values: Values, formikHelpers: FormikHelpers<Values>) => void;
  loading: boolean;
  defaultValues?: IConditionForUpdate;
  title: string;
  showTrackingPlatform?: boolean;
}

const PatientConditionModalForm: FC<PatientConditionModalFormProps> = ({
  open,
  loading,
  onCancelForm,
  onSubmit,
  defaultValues,
  title,
  showTrackingPlatform,
}) => {
  const { t } = useTranslation();
  const formik = useFormik<Values>({
    initialValues:
      patientConditionValidationSchema(showTrackingPlatform).cast(
        defaultValues,
      ),
    enableReinitialize: true,
    onSubmit,
    validateOnChange: false,
    validationSchema: patientConditionValidationSchema(showTrackingPlatform),
  });

  const { submitForm, resetForm } = formik;

  const onCancel = () => {
    resetForm();
    onCancelForm();
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  return (
    <>
      {open && (
        <MasterModal
          title={title}
          isOpen={open}
          toggle={onCancel}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
          footer={
            <>
              <BaseButton
                dataCy="form-cancel-button"
                label={t('controls.cancel')}
                type="secondary"
                onClick={onCancel}
                disabled={loading}
              />
              <BaseButton
                dataCy="form-save-button"
                label={t('controls.save')}
                onClick={submitForm}
                disabled={loading}
                loading={loading}
              />
            </>
          }
        >
          <BaseForm
            formik={formik}
            withControls
            plaintext={false}
            readonly={false}
          >
            {showTrackingPlatform && (
              <BaseFormItem
                name="trackingPlatform"
                label={t('labels.program')}
                required
                isBlackLabel
              >
                <BaseSelectBox
                  options={TRACKING_PLATFORM_OPTIONS}
                  placeholder={t('labels.selectProgram')}
                />
              </BaseFormItem>
            )}
            <BaseFormItem
              name="code"
              required
              label={t('labels.icdCode')}
              isBlackLabel
            >
              <ConditionSearchSelect
                onChange={(value, option) => {
                  formik.setFieldValue('code', option.label);
                  formik.setFieldValue('diagnoseGuid', option.guid);
                  formik.setFieldValue('description', option.description);
                }}
                disabled={typeof defaultValues !== 'undefined'}
              />
            </BaseFormItem>
            <BaseFormItem
              name="description"
              required
              label={t('labels.description')}
              isBlackLabel
            >
              <BaseTextArea rows={10} />
            </BaseFormItem>
          </BaseForm>
        </MasterModal>
      )}
    </>
  );
};
export default PatientConditionModalForm;
